import { defineMessages, type MessageDescriptor } from 'react-intl';

export type SurveyErrorKey =
	| 'SURVEY_CREATION_LIMIT_EXCEEDED'
	| 'SURVEY_TITLE_TOO_LONG'
	| 'SURVEY_QUESTION_LIMIT_EXCEEDED'
	| 'SURVEY_TEXT_ANSWER_WORD_LIMIT_EXCEEDED'
	| 'SURVEY_RESTRICTION_SIZE_LIMIT_EXCEEDED'
	| 'SURVEY_PERMISSION_SIZE_LIMIT_EXCEEDED'
	| 'SURVEY_FORM_TEMPLATE_INVALID'
	| 'SURVEY_CATEGORY_LIMIT_EXCEEDED'
	| 'SURVEY_CATEGORY_INVALID'
	| 'SURVEYS_FEATURE_DISABLED'
	| 'PORTAL_NOT_FOUND'
	| 'JIRA_ERROR'
	// Catch all case
	| string;

export interface FlagContent {
	title: string;
	description: string;
}

const messages = defineMessages({
	generic: {
		id: 'conversation-assistant-ui-components.survey-preview-card-error-message',
		defaultMessage: "We couldn't create the survey. Try again.",
		description: 'Error message shown when a survey fails to create via AI.',
	},
	creationLimitExceeded: {
		id: 'conversation-assistant-ui-components.survey-preview-card-error-creation-limit-exceeded',
		defaultMessage:
			'Survey limit reached in your current space. Delete an existing survey and try again.',
		description: 'Error message shown when the project has reached its maximum number of surveys.',
	},
	titleTooLong: {
		id: 'conversation-assistant-ui-components.survey-preview-card-error-title-too-long',
		defaultMessage: 'This title exceeds the character limit. Ask Rovo to use a shorter name.',
		description: 'Error message shown when the survey name exceeds the allowed character limit.',
	},
	questionLimitExceeded: {
		id: 'conversation-assistant-ui-components.survey-preview-card-error-question-limit-exceeded',
		defaultMessage: 'Question limit reached. Ask Rovo to remove a few questions.',
		description: 'Error message shown when the survey has reached its maximum number of questions.',
	},
	featureDisabled: {
		id: 'conversation-assistant-ui-components.survey-preview-card-error-feature-disabled',
		defaultMessage:
			"The Surveys feature isn't turned on in your space. Ask your space admin to turn it on.",
		description: 'Error message shown when the surveys feature is disabled for the project.',
	},
	flagTitle: {
		id: 'conversation-assistant-ui-components.survey-preview-card-error-flag-title',
		defaultMessage: "Couldn't create this survey",
		description: 'Flag title shown when JSM survey creation fails.',
	},
});

const errorKeyToMessage: Record<string, MessageDescriptor> = {
	SURVEY_CREATION_LIMIT_EXCEEDED: messages.creationLimitExceeded,
	SURVEY_TITLE_TOO_LONG: messages.titleTooLong,
	SURVEY_QUESTION_LIMIT_EXCEEDED: messages.questionLimitExceeded,
	SURVEYS_FEATURE_DISABLED: messages.featureDisabled,
};

export class SurveyApiError extends Error {
	readonly errorKey: SurveyErrorKey;

	constructor(errorKey: SurveyErrorKey, message: string) {
		super(message);
		this.errorKey = errorKey;
	}
}

export const getSurveyCreationFlagContent = (
	errorKey: string | undefined,
	formatMessage: (descriptor: MessageDescriptor) => string,
): FlagContent => {
	const descriptor = errorKey
		? (errorKeyToMessage[errorKey] ?? messages.generic)
		: messages.generic;
	return {
		title: formatMessage(messages.flagTitle),
		description: formatMessage(descriptor),
	};
};

export const parseSurveyApiError = async (res: Response): Promise<SurveyApiError | null> => {
	let errorKey: SurveyErrorKey | undefined;
	let message: string | undefined;
	try {
		const json = await res.json();
		errorKey = json?.error;
		message = json?.message;
	} catch {
		return null;
	}

	if (errorKey && typeof errorKey === 'string' && message && typeof message === 'string') {
		return new SurveyApiError(errorKey, message);
	}

	return null;
};
